/**
 * Data Protection Service
 *
 * Centralized data protection, backup, and recovery system for ClothedEye
 * Implements the safety mechanisms outlined in ETSY_DATA_INTEGRATION_ANALYSIS.md
 */

import logger from '../utils/logger.js';

class DataProtectionService {
  constructor() {
    this.baseUrl = '/.netlify/functions';

    // Field Classifications for Data Protection
    this.FIELD_CLASSIFICATIONS = {
      local_only: [
        'svgPath',
        'svgPaths',
        'svgTitles',
        'dimensions',
        'publicVisible',
        'isSet',
        'displayOrder',
        'packType',
        'isPack',
      ],
      etsy_only: [
        'title',
        'description',
        'etsyUrl',
        'state',
        'views',
        'num_favorers',
        'listing_data',
        'inventory_data',
      ],
      manual_override: [
        'category', // Only update if currently null
      ],
      computed: [
        'available_sizes',
        'available_colors',
        'first_image_url',
        'largest_size_image_url',
        'all_image_urls',
      ],
    };
  }

  /**
   * Initialize backup system and ensure tables exist
   */
  async initialize() {
    try {
      logger.log('🔧 Initializing data protection service...');
      await this.ensureBackupTable();
      logger.log('✅ Data protection service initialized');
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize data protection service:', error);
      return false;
    }
  }

  /**
   * Create backup table if it doesn't exist
   */
  async ensureBackupTable() {
    try {
      const response = await fetch(`${this.baseUrl}/local-db/create-backup-table`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        logger.warn('⚠️ Backup table creation skipped (may already exist)');
      } else {
        logger.log('✅ Backup table ensured');
      }
    } catch (error) {
      logger.warn('⚠️ Could not ensure backup table:', error.message);
    }
  }

  /**
   * Create comprehensive backup of listing data before update
   */
  async createBackup(listingId, backupType = 'pre_etsy_update') {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const backupId = `${listingId}_${timestamp}`;

    logger.log(`💾 Creating ${backupType} backup ${backupId}...`);

    try {
      // Get current unified sticker data
      const stickerResponse = await fetch(`${this.baseUrl}/local-db/unified-stickers/${listingId}`);
      const stickerData = stickerResponse.ok ? await stickerResponse.json() : null;

      // Get current etsy listing data
      const etsyResponse = await fetch(`${this.baseUrl}/local-db/etsy-listing-data/${listingId}`);
      const etsyData = etsyResponse.ok ? await etsyResponse.json() : null;

      if (!stickerData && !etsyData) {
        throw new Error(`No data found for listing ${listingId} to backup`);
      }

      // Create comprehensive backup record
      const backup = {
        backup_id: backupId,
        listing_id: listingId,
        timestamp: timestamp,
        backup_type: backupType,
        sticker_data: stickerData?.data || null,
        etsy_data: etsyData?.data || null,
        local_fields_count: 0,
        etsy_fields_count: 0,
      };

      // Count field types for reporting
      if (backup.sticker_data) {
        const localFields = this.FIELD_CLASSIFICATIONS.local_only;
        backup.local_fields_count = localFields.filter(
          (field) => backup.sticker_data[field] !== undefined && backup.sticker_data[field] !== null
        ).length;

        const etsyFields = this.FIELD_CLASSIFICATIONS.etsy_only;
        backup.etsy_fields_count = etsyFields.filter(
          (field) => backup.sticker_data[field] !== undefined && backup.sticker_data[field] !== null
        ).length;
      }

      // Store backup
      const storeResponse = await fetch(`${this.baseUrl}/local-db/store-backup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backup),
      });

      if (!storeResponse.ok) {
        throw new Error(`Failed to store backup: ${storeResponse.statusText}`);
      }

      logger.log(
        `✅ Backup created: ${backupId} (${backup.local_fields_count} local, ${backup.etsy_fields_count} etsy fields)`
      );
      return backup;
    } catch (error) {
      console.error(`❌ Backup failed for ${listingId}:`, error);
      throw error;
    }
  }

  /**
   * Validate that local data integrity is maintained after update
   */
  async validateDataIntegrity(listingId, preUpdateBackup) {
    logger.log(`🔍 Validating data integrity for listing ${listingId}...`);

    try {
      // Get current data after update
      const currentResponse = await fetch(`${this.baseUrl}/local-db/unified-stickers/${listingId}`);
      if (!currentResponse.ok) {
        throw new Error('Could not fetch current data for validation');
      }

      const currentData = await currentResponse.json();
      const validation = {
        listing_id: listingId,
        timestamp: new Date().toISOString(),
        local_data_preserved: true,
        etsy_data_updated: true,
        issues: [],
        warnings: [],
      };

      // Check that local-only fields weren't lost
      const localFields = this.FIELD_CLASSIFICATIONS.local_only;
      for (const field of localFields) {
        const beforeValue = preUpdateBackup.sticker_data?.[field];
        const afterValue = currentData.data?.[field];

        // If we had local data before but it's gone now, that's a critical issue
        if (beforeValue && !afterValue) {
          validation.local_data_preserved = false;
          validation.issues.push(`Lost local field: ${field} (was: ${beforeValue})`);
        }
        // If values changed unexpectedly, that's a warning
        else if (beforeValue && afterValue && beforeValue !== afterValue) {
          validation.warnings.push(
            `Local field changed: ${field} (${beforeValue} → ${afterValue})`
          );
        }
      }

      // Check that Etsy fields were updated appropriately
      const etsyFields = ['title', 'description', 'etsyUrl'];
      const hasEtsyUpdates = etsyFields.some((field) => currentData.data?.[field]);
      if (!hasEtsyUpdates) {
        validation.etsy_data_updated = false;
        validation.issues.push('No Etsy data appears to be present after update');
      }

      // Overall validation result
      validation.success = validation.local_data_preserved && validation.etsy_data_updated;

      if (validation.success) {
        logger.log(`✅ Data integrity validation passed for ${listingId}`);
        if (validation.warnings.length > 0) {
          logger.warn(`⚠️ Validation warnings: ${validation.warnings.join(', ')}`);
        }
      } else {
        console.error(`❌ Data integrity validation failed for ${listingId}:`, validation.issues);
      }

      return validation;
    } catch (error) {
      console.error(`❌ Data integrity validation failed for ${listingId}:`, error);
      throw error;
    }
  }

  /**
   * Recover local data from a backup
   */
  async recoverFromBackup(listingId, backupId = null) {
    logger.log(`🔄 Recovering local data for listing ${listingId}...`);

    try {
      let backup;

      if (backupId) {
        // Recover from specific backup
        backup = await this.getBackup(backupId);
      } else {
        // Recover from most recent backup
        backup = await this.getMostRecentBackup(listingId);
      }

      if (!backup) {
        throw new Error(`No backup found for listing ${listingId}`);
      }

      if (!backup.sticker_data) {
        logger.warn(`⚠️ No sticker data in backup ${backup.backup_id}`);
        return { success: false, message: 'No sticker data to restore' };
      }

      // Extract only local-only fields for restoration
      const localFields = this.FIELD_CLASSIFICATIONS.local_only;
      const restoreData = { etsyListingId: listingId };

      localFields.forEach((field) => {
        if (backup.sticker_data[field] !== undefined) {
          restoreData[field] = backup.sticker_data[field];
        }
      });

      if (Object.keys(restoreData).length <= 1) {
        // Only etsyListingId
        logger.warn(`⚠️ No local data to restore for ${listingId}`);
        return { success: false, message: 'No local data fields to restore' };
      }

      // Apply restoration
      const response = await fetch(`${this.baseUrl}/local-db/unified-stickers/${listingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(restoreData),
      });

      if (!response.ok) {
        throw new Error(`Restore operation failed: ${response.statusText}`);
      }

      logger.log(`✅ Local data restored for ${listingId} from backup ${backup.backup_id}`);
      logger.log(
        `   Restored fields: ${Object.keys(restoreData)
          .filter((k) => k !== 'etsyListingId')
          .join(', ')}`
      );

      return {
        success: true,
        backup_used: backup.backup_id,
        restored_fields: Object.keys(restoreData).filter((k) => k !== 'etsyListingId'),
        timestamp: backup.timestamp,
      };
    } catch (error) {
      console.error(`❌ Recovery failed for ${listingId}:`, error);
      throw error;
    }
  }

  /**
   * Get specific backup by ID
   */
  async getBackup(backupId) {
    try {
      const response = await fetch(`${this.baseUrl}/local-db/get-backup/${backupId}`);
      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error(`❌ Failed to get backup ${backupId}:`, error);
      return null;
    }
  }

  /**
   * Get most recent backup for a listing
   */
  async getMostRecentBackup(listingId) {
    try {
      const response = await fetch(`${this.baseUrl}/local-db/get-recent-backup/${listingId}`);
      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error(`❌ Failed to get recent backup for ${listingId}:`, error);
      return null;
    }
  }

  /**
   * List all available backups for a listing
   */
  async listBackups(listingId) {
    try {
      const response = await fetch(`${this.baseUrl}/local-db/list-backups/${listingId}`);
      if (!response.ok) {
        throw new Error(`Failed to list backups: ${response.statusText}`);
      }

      const backups = await response.json();

      logger.log(`📋 Available backups for ${listingId}:`);
      backups.forEach((backup) => {
        logger.log(`  ${backup.backup_id} (${backup.backup_type}) - ${backup.timestamp}`);
      });

      return backups;
    } catch (error) {
      console.error(`❌ Failed to list backups for ${listingId}:`, error);
      return [];
    }
  }

  /**
   * Validate data completeness for a listing
   */
  async validateDataCompleteness(listingId) {
    logger.log(`🔍 Validating data completeness for listing ${listingId}...`);

    const validation = {
      listing_id: listingId,
      timestamp: new Date().toISOString(),
      checks: {},
      overall_status: 'UNKNOWN',
    };

    try {
      // Check sticker data presence
      validation.checks.hasSticker = await this.checkStickerData(listingId);

      // Check Etsy data presence
      validation.checks.hasEtsyData = await this.checkEtsyData(listingId);

      // Check price data
      validation.checks.hasPrice = await this.checkPriceData(listingId);

      // Check image data
      validation.checks.hasImages = await this.checkImageData(listingId);

      // Check inventory data
      validation.checks.hasInventory = await this.checkInventoryData(listingId);

      // Check local data preservation
      validation.checks.hasLocalData = await this.checkLocalDataIntegrity(listingId);

      // Overall assessment
      const criticalChecks = ['hasSticker', 'hasEtsyData', 'hasPrice'];
      const passed = criticalChecks.every((check) => validation.checks[check]);
      const hasWarnings = !validation.checks.hasImages || !validation.checks.hasInventory;

      if (passed && !hasWarnings) {
        validation.overall_status = 'COMPLETE';
      } else if (passed) {
        validation.overall_status = 'COMPLETE_WITH_WARNINGS';
      } else {
        validation.overall_status = 'INCOMPLETE';
      }

      logger.log(`📊 Data completeness for ${listingId}: ${validation.overall_status}`);
      return validation;
    } catch (error) {
      validation.error = error.message;
      validation.overall_status = 'ERROR';
      console.error(`❌ Validation failed for ${listingId}:`, error);
      return validation;
    }
  }

  /**
   * Check if sticker data exists
   */
  async checkStickerData(listingId) {
    try {
      const response = await fetch(`${this.baseUrl}/local-db/unified-stickers/${listingId}`);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if Etsy data exists
   */
  async checkEtsyData(listingId) {
    try {
      const response = await fetch(`${this.baseUrl}/local-db/etsy-listing-data/${listingId}`);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if price data exists
   */
  async checkPriceData(listingId) {
    try {
      const response = await fetch(`${this.baseUrl}/local-db/etsy-listing-data/${listingId}`);
      if (!response.ok) return false;

      const data = await response.json();
      const hasPrice =
        data.data?.listing_data && JSON.stringify(data.data.listing_data).includes('price');
      return hasPrice;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if image data exists
   */
  async checkImageData(listingId) {
    try {
      const response = await fetch(`${this.baseUrl}/local-db/unified-stickers/${listingId}`);
      if (!response.ok) return false;

      const data = await response.json();
      return !!(data.data?.first_image_url || data.data?.largest_size_image_url);
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if inventory data exists
   */
  async checkInventoryData(listingId) {
    try {
      const response = await fetch(`${this.baseUrl}/local-db/etsy-listing-data/${listingId}`);
      if (!response.ok) return false;

      const data = await response.json();
      return !!data.data?.inventory_data;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if local data integrity is maintained
   */
  async checkLocalDataIntegrity(listingId) {
    try {
      const response = await fetch(`${this.baseUrl}/local-db/unified-stickers/${listingId}`);
      if (!response.ok) return false;

      const data = await response.json();
      const localFields = this.FIELD_CLASSIFICATIONS.local_only;

      // Return true if ANY local data exists (means it wasn't overwritten)
      return localFields.some(
        (field) => data.data?.[field] !== undefined && data.data[field] !== null
      );
    } catch (error) {
      return false;
    }
  }

  /**
   * Get health statistics for the protection system
   */
  async getHealthStats() {
    try {
      const response = await fetch(`${this.baseUrl}/local-db/backup-stats`);
      if (!response.ok) {
        return { error: 'Could not fetch backup statistics' };
      }

      return await response.json();
    } catch (error) {
      return { error: error.message };
    }
  }
}

// Create and export singleton instance
const dataProtectionService = new DataProtectionService();

export default dataProtectionService;
